import React from 'react';

const Disclaimer = ({ onBack }) => {
  return (
    <div className="min-h-screen py-10 px-4" style={{ backgroundColor: 'var(--bg-base)' }}>
      <div className="max-w-3xl mx-auto">
        {/* Back button */}
        <button
          onClick={onBack}
          className="mb-6 flex items-center gap-2 text-sm font-medium transition-colors"
          style={{ color: 'var(--accent-primary)' }}
        >
          <span>←</span> Back
        </button>

        <div className="p-8 rounded-2xl" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }}>
          <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Disclaimer</h1>
          <p className="text-sm mb-1" style={{ color: 'var(--text-muted)' }}>Effective Date: February 25, 2026</p>
          <p className="text-sm mb-8" style={{ color: 'var(--text-muted)' }}>Last Updated: February 25, 2026</p>

          <div className="space-y-6 text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>

            {/* 1 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>1. General Disclaimer</h2>
              <p>
                Founders+ is a community platform designed to help founders connect, collaborate, and track their entrepreneurial
                journey. The Platform is provided on an "AS IS" and "AS AVAILABLE" basis without warranties of any kind, either
                express or implied, including but not limited to implied warranties of merchantability, fitness for a particular
                purpose, and non-infringement. Your use of the Platform is entirely at your own risk.
              </p>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>2. No Professional Advice</h2>
              <p>
                Nothing on this Platform constitutes professional financial, legal, business, tax, investment, or medical advice.
                All content — including posts, discussions, AI-generated suggestions, momentum scores, and community-shared
                information — is for general informational and educational purposes only.
              </p>
              <p className="mt-2">
                You should consult qualified professionals before making any financial, legal, or business decisions. Founders+
                is not a substitute for professional advice tailored to your specific situation. Reliance on any information
                provided through the Platform is solely at your own risk.
              </p>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>3. AI-Generated Content</h2>
              <p>
                The Platform includes AI-powered features such as the AI Co-Founder Assistant, momentum scoring, and content
                suggestions. AI-generated content is provided for informational purposes only and may contain errors, inaccuracies,
                or outdated information. You should not rely solely on AI-generated content for important decisions.
              </p>
              <p className="mt-2">
                The Platform makes no representations or warranties regarding the accuracy, reliability, or completeness of
                AI-generated content. AI outputs do not constitute professional, legal, financial, or business advice.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>4. User-Generated Content</h2>
              <p>
                The Platform hosts content created by its users. Founders+ does not endorse, verify, or guarantee the accuracy,
                completeness, or reliability of any user-generated content. Views expressed by users are their own and do not
                represent the views or positions of Founders+.
              </p>
              <p className="mt-2">
                You acknowledge that you may be exposed to content that is inaccurate, misleading, incomplete, or objectionable.
                Founders+ shall not be liable for any such content or for any reliance placed upon it.
              </p>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>5. No Guarantees of Results</h2>
              <p>
                Founders+ does not guarantee any specific outcomes from using the Platform. Tools such as build trackers,
                priority matrices, focus timers, momentum scores, decision journals, and other features are productivity aids —
                not guarantees of business success, investment returns, or any other outcome. Individual results depend on many
                factors beyond the scope of this Platform.
              </p>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>6. Community Events Disclaimer</h2>
              <p>
                The Platform provides tools for users to create, promote, and manage community events. Founders+ acts solely
                as a neutral facilitation platform. We do not organize, sponsor, endorse, or guarantee any events listed on
                the Platform.
              </p>
              <p className="mt-2">
                Event Organizers are solely responsible for the planning, execution, safety, and legal compliance of their
                events. Founders+ assumes no liability for:
              </p>
              <ul className="list-disc pl-5 space-y-1 mt-1">
                <li>Event cancellations, postponements, or modifications</li>
                <li>Personal injury, illness, or death occurring at or related to any event</li>
                <li>Property damage or loss</li>
                <li>Inadequate safety measures, facilities, or insurance</li>
                <li>Misconduct, fraud, or negligence by Organizers, Participants, or venue operators</li>
                <li>Regulatory violations or unlawful activities at events</li>
              </ul>
              <p className="mt-2">
                Participants attend and engage with all events entirely at their own risk. Please review the full Events
                provisions in our Terms of Use and Service Agreement.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>7. Third-Party Links and Services</h2>
              <p>
                The Platform may contain links to, or integrations with, third-party websites or services not owned or
                controlled by Founders+. We have no control over, and assume no responsibility for, the content, privacy
                policies, accuracy, or practices of any third-party sites or services. You access them entirely at your own risk.
              </p>
              <p className="mt-2">
                The inclusion of any third-party link or integration does not imply endorsement, sponsorship, or affiliation
                by Founders+.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>8. Service Availability</h2>
              <p>
                We do not guarantee that the Platform will be available at all times or without interruption, error, or delay.
                We may perform scheduled or unscheduled maintenance, updates, or experience technical downtime. Founders+ is
                not liable for any loss, damage, or inconvenience caused by the unavailability or interruption of the Platform.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>9. Data Loss</h2>
              <p>
                While we implement reasonable technical measures to protect your data, we are not liable for any data loss
                arising from technical failures, security incidents, or other unforeseen circumstances. We recommend maintaining
                your own backups of any important information stored on the Platform.
              </p>
            </section>

            {/* 10 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>10. Third-Party Conduct</h2>
              <p>
                Founders+ is not responsible for the conduct, representations, actions, or omissions of other users,
                event organizers, participants, venue operators, or any third parties interacting with the Platform.
                All interactions with other users are at your own risk.
              </p>
            </section>

            {/* 11 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>11. Limitation of Damages</h2>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, FOUNDERS+ AND ITS OFFICERS, DIRECTORS, EMPLOYEES,
                AND AGENTS SHALL NOT BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR EXEMPLARY
                DAMAGES, INCLUDING BUT NOT LIMITED TO DAMAGES FOR LOSS OF PROFITS, GOODWILL, DATA, BUSINESS OPPORTUNITIES,
                OR OTHER INTANGIBLE LOSSES, RESULTING FROM YOUR USE OF OR INABILITY TO USE THE PLATFORM, REGARDLESS OF
                THE THEORY OF LIABILITY.
              </p>
              <p className="mt-2">
                This limitation applies even if Founders+ has been advised of the possibility of such damages and
                notwithstanding any failure of essential purpose of any limited remedy.
              </p>
            </section>

            {/* 12 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>12. Indemnification</h2>
              <p>
                By using the Platform, you agree to indemnify and hold harmless Founders+, its affiliates, officers,
                directors, employees, and agents from any claims, damages, losses, or liabilities arising from your use
                of the Platform, reliance on any content or information available through it, or your participation in
                or organization of any events. This applies to the full extent permitted by applicable law.
              </p>
            </section>

            {/* 13 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>13. Changes to This Disclaimer</h2>
              <p>
                We reserve the right to modify this Disclaimer at any time. Material changes will be communicated via
                email or prominent notice on the Platform. Your continued use of the Platform after changes are posted
                constitutes acceptance of the updated Disclaimer.
              </p>
            </section>

            {/* 14 */}
            <section>
              <h2 className="text-lg font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>14. Contact Information</h2>
              <p>If you have questions about this Disclaimer, contact us at:</p>
              <p className="mt-2">
                <strong style={{ color: 'var(--text-primary)' }}>Email:</strong>{' '}
                <a href="mailto:foundersplus2025@gmail.com" style={{ color: 'var(--accent-primary)' }}>
                  foundersplus2025@gmail.com
                </a>
              </p>
              <p className="mt-1">
                <strong style={{ color: 'var(--text-primary)' }}>Response Time:</strong> We respond to inquiries within fifteen (15) business days.
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Disclaimer;
